<script setup>
import { computed, ref } from 'vue';
import UserNotificationSettings from 'dashboard/api/userNotificationSettings';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({ pushFlags: { type: Array, default: () => [] } });
const copy = {
  title: 'Push entre empresas',
  description:
    'Receba avisos mesmo enquanto usa outra empresa. A permissão deste navegador vale para seu usuário; os tipos de aviso são definidos em cada empresa.',
  dialogTitle: 'Push nas minhas empresas',
  summary: count =>
    `${count} tipos de aviso serão ativados; os demais tipos de mensagem serão desligados.`,
  active: count => `${count} tipos de aviso ativos`,
};
const TYPES = [
  'conversation_creation',
  'conversation_assignment',
  'conversation_mention',
  'assigned_conversation_new_message',
  'participating_conversation_new_message',
  'unassigned_conversation_new_message',
  'other_assigned_conversation_new_message',
];
const flags = computed(() =>
  props.pushFlags.filter(flag => TYPES.some(type => flag === `push_${type}`))
);
const dialog = ref(null);
const companies = ref([]);
const selectedIds = ref([]);
const busy = ref(false);
const snapshot = ref([]);

const open = async () => {
  if (busy.value) return;
  busy.value = true;
  try {
    const { data } = await UserNotificationSettings.companies();
    companies.value = data.companies;
    selectedIds.value = [];
    snapshot.value = [...flags.value];
    dialog.value.open();
  } catch {
    useAlert('Não foi possível carregar suas empresas. Tente novamente.');
  } finally {
    busy.value = false;
  }
};

const toggle = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(value => value !== id)
    : [...selectedIds.value, id];
};

const apply = async () => {
  if (busy.value || !selectedIds.value.length) return;
  busy.value = true;
  try {
    await UserNotificationSettings.applyToCompanies({
      account_ids: selectedIds.value,
      selected_push_flags: snapshot.value,
    });
    useAlert('Preferências de push aplicadas às empresas selecionadas.', {
      variant: 'positive',
    });
    dialog.value.close();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível aplicar as preferências. Tente novamente.'
    );
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
    <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
      {{ copy.title }}
    </h4>
    <p class="mb-3 text-sm text-n-slate-11">
      {{ copy.description }}
    </p>
    <Button
      type="button"
      label="Configurar minhas empresas"
      size="sm"
      :is-loading="busy"
      @click="open"
    />
    <Dialog
      ref="dialog"
      :title="copy.dialogTitle"
      description="Aplique os tipos de mensagem e atribuição desta tela às empresas selecionadas. E-mail, etiquetas, SLA e conversas silenciadas mantêm suas configurações próprias."
      confirm-button-label="Aplicar às selecionadas"
      cancel-button-label="Cancelar"
      :disable-confirm-button="busy || !selectedIds.length"
      :is-loading="busy"
      :close-on-click-outside="!busy"
      overflow-y-auto
      sticky-header-footer
      solid
      @confirm="apply"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ copy.summary(snapshot.length) }}
      </p>
      <Button
        type="button"
        label="Selecionar todas as empresas"
        size="sm"
        variant="outline"
        :disabled="busy"
        @click="selectedIds = companies.map(company => company.id)"
      />
      <label
        v-for="company in companies"
        :key="company.id"
        class="flex items-center gap-3 rounded-lg border border-n-weak p-3"
      >
        <Checkbox
          :model-value="selectedIds.includes(company.id)"
          :disabled="busy"
          @change="toggle(company.id)"
        />
        <span class="min-w-0 text-sm">
          <span class="block break-words font-medium text-n-slate-12">{{
            company.name
          }}</span>
          <span class="text-n-slate-11">{{
            copy.active(
              company.selected_push_flags.filter(flag =>
                TYPES.some(type => flag === `push_${type}`)
              ).length
            )
          }}</span>
        </span>
      </label>
    </Dialog>
  </div>
</template>
